import { COLORS } from "@/data/main-color";
import { motion } from "framer-motion";
import { ArrowUpRight, Landmark } from "lucide-react";
import Link from "next/link";
import { FeaturePill } from "./Sub-Components";

export default function HeroText({ isAr }: { isAr: boolean }) {
  const features = isAr
    ? ["مطور عقاري", "شريك بنكي", "وكيل معتمد", "مسوّق"]
    : ["Developer", "Bank Partner", "Certified Broker", "Marketer"];

  return (
    <div className="relative z-4 w-full mx-auto px-14 pt-30 pb-25">
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="inline-flex items-center gap-2.5 mb-9"
      >
        <div
          className={`w-8 h-px bg-gradient-to-${isAr ? "left" : "right"} from-[#C8A96E] to-transparent`}
        />
        <span className="text-[11px] tracking-[4px] text-[#C8A96E] uppercase font-cairo">
          {isAr
            ? "بوابة المؤسسات العقارية والبنكية"
            : "Institutional Real Estate & Banking Portal"}
        </span>
      </motion.div>

      <div className="max-w-170">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.9,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1],
          }}
          className={`text-[clamp(46px,6.5vw,88px)] ${isAr ? "font-black" : "font-bold"} leading-[1.04] ${isAr ? "tracking-[-0.5px]" : "tracking-[-3px]"} mb-3 ${COLORS.cream}`}
        >
          {isAr ? "دورة حياة العقار" : "The Full Real Estate"}
        </motion.h1>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.9,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1],
          }}
          className={`text-[clamp(46px,6.5vw,88px)] ${isAr ? "font-black" : "font-bold"} leading-[1.04] ${isAr ? "tracking-[-0.5px]" : "tracking-[-3px]"} mb-8`}
        >
          <span className="shimmer-text">
            {isAr ? "من الفكرة للتمويل" : "Lifecycle, Financed"}
          </span>
        </motion.h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.35, ease: "easeOut" }}
          className={`h-px w-20 mb-7 bg-gradient-to-${isAr ? "left" : "right"} from-[#C8A96E] to-transparent ${isAr ? "origin-right" : "origin-left"}`}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className={`text-[17px] leading-[1.85] ${COLORS.muted} mb-12 font-cairo font-light max-w-130`}
        >
          {isAr
            ? "المنصة الأولى التي تدمج دورة حياة العقار بالكامل؛ من المخطط الهندسي للمطور، وحتى المزادات وتصفية الأصول والتمويل البنكي للمؤسسات."
            : "The first platform integrating the full property lifecycle; from developer blueprints, to asset liquidation auctions and institutional bank financing."}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.52 }}
        className="flex flex-wrap gap-3.5 mb-16 font-cairo"
      >
        <Link
          href="/auth/login"
          className="cta-gold inline-flex items-center gap-2 px-10 py-4 bg-linear-to-br from-[#C8A96E] via-[#F0D57A] to-[#B8922A] bg-size-[200%_auto] text-[#06080D] font-extrabold text-[15px] border-none rounded-md cursor-pointer tracking-[0.5px] transition-all duration-350 ease-in-out shadow-[0_8px_32px_rgba(200,169,110,0.25)] clip-premium hover:bg-right hover:-translate-y-0.75 hover:shadow-[0_20px_56px_rgba(200,169,110,0.45)]"
        >
          {isAr ? "إنضم معنا" : "Institution Login"}
          <ArrowUpRight size={16} />
        </Link>

        <button className="cta-ghost inline-flex items-center gap-2 px-9 py-3.5 bg-transparent ${C.cream} font-semibold text-[15px] border border-[#EDE5D6]/15 rounded-md cursor-pointer tracking-[0.5px] transition-all duration-300 ease-in-out clip-premium hover:border-[#C8A96E] hover:text-[#C8A96E] hover:bg-[#C8A96E]/15">
          <Landmark size={14} />
          {isAr ? "خدمات التمويل" : "Financing Services"}
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.65 }}
        className="flex flex-wrap gap-2.5 mb-16 font-cairo"
      >
        <span
          className={`text-[11px] ${COLORS.muted} tracking-[2px] uppercase self-center ${isAr ? "ml-2" : "mr-2"}`}
        >
          {isAr ? "منظومة موحدة لـ" : "Built for"}
        </span>
        {features.map((f) => (
          <FeaturePill key={f} text={f} />
        ))}
      </motion.div>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.7 }}
        className={`h-px max-w-140 bg-gradient-to-${isAr ? "left" : "right"} from-[#C8A96E]/25 to-transparent mb-9 ${isAr ? "origin-right" : "origin-left"}`}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        className="flex items-center gap-4 flex-wrap font-cairo"
      >
        <span
          className={`text-[12px] ${COLORS.muted} tracking-[1.5px] uppercase`}
        >
          {isAr
            ? "نظام آمن معتمد من كبرى المؤسسات المالية في المملكة"
            : "Secure system certified by major KSA financial institutions"}
        </span>
        <div className="flex -space-x-2.5">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-8 h-8 rounded-full border-2 border-[#06080D] flex items-center justify-center z-[5-i]"
              style={{
                background: `linear-gradient(135deg, hsl(${200 + i * 30},40%,30%), hsl(${220 + i * 25},50%,25%))`,
                marginLeft: i > 0 && !isAr ? -10 : 0,
                marginRight: i > 0 && isAr ? -10 : 0,
              }}
            >
              {i === 0 && <Landmark size={14} className="text-[#C8A96E]/60" />}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
